import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, TimelineEmptyIllustration } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterRecordEmptyState from '@/matter-asset-common/asset/MatterRecordEmptyState'
import { selectMatterById } from '@/matter/store/selectors'
import { canEditTimeline } from '@/matter/utils/matter-permissions'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { TimelineAssetActions } from '../store/slice'
import { kTimelineOwnerOnlyBuildMessage, kTimelineOwnerOnlyBuildOwnerMessage, kTimelineEditorBuildMessage } from '../constants'
import MatterRecordReadOnlyNotice from '@/matter-asset-common/asset/MatterRecordReadOnlyNotice'
import { useMatterOwnerName } from '@/matter/hooks/useMatterOwnerName'
import EditingLeaseNotice from '@/editing-leases/components/EditingLeaseNotice'
import { useTimelineEditingLease } from '../hooks/useTimelineEditingLease'
import { isHeldByAnotherUser } from '@/editing-leases/utils/is-held-by-another-user'

interface TimelineAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no timeline yet. Thin
 * wrapper over the shared MatterRecordEmptyState that supplies the timeline copy, illustration, CTA, and
 * the first-generation-error banner (shown after a failed Generate).
 */
export default function TimelineAssetEmptyState(props: TimelineAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  // An unresolved matter must keep the build CTA disabled until the viewer's membership is known.
  const { enabled: editorEnabled, loading: editorLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)
  const isReadOnly = !canEditTimeline(matter, editorEnabled && !editorLoading)
  const lease = useTimelineEditingLease()
  const leaseReadOnly = !isReadOnly && !lease.canWrite
  const heldByAnotherEditor = isHeldByAnotherUser(lease.lease)
  const needsStartEditing = leaseReadOnly && lease.enabled && !lease.initializing && !heldByAnotherEditor
  const [openingFilesForMatter, setOpeningFilesForMatter] = useState<string | null>(null)
  const showReadOnlyNotice = isReadOnly && !!matter && !editorLoading
  const buildMessage = editorEnabled && !editorLoading ? kTimelineEditorBuildMessage : kTimelineOwnerOnlyBuildMessage
  const ownerName = useMatterOwnerName(matterId)
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  useEffect(() => {
    if (!openingFilesForMatter) return
    if (openingFilesForMatter !== matterId) {
      setOpeningFilesForMatter(null)
      return
    }
    if (lease.pending) return
    setOpeningFilesForMatter(null)
    if (!isReadOnly && lease.canWrite) navigate(matterPaths.timeline.addFiles(matterId))
  }, [openingFilesForMatter, matterId, lease.pending, lease.canWrite, isReadOnly, navigate])

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {(showReadOnlyNotice || (leaseReadOnly && heldByAnotherEditor)) && (
        <div className="pt-4 px-4">
          {leaseReadOnly ? (
            <EditingLeaseNotice lease={lease.lease} shape="strip" />
          ) : (
            <MatterRecordReadOnlyNotice
              icon={<Lock />}
              headline={ownerName ? `${ownerName} is the owner of this matter.` : undefined}
              message={
                editorEnabled && !editorLoading ? kTimelineEditorBuildMessage : ownerName ? kTimelineOwnerOnlyBuildOwnerMessage : kTimelineOwnerOnlyBuildMessage
              }
            />
          )}
        </div>
      )}
      <MatterRecordEmptyState
        illustration={<TimelineEmptyIllustration className="h-10 w-[63px] shrink-0" />}
        title="Build your timeline"
        body="Build a complete timeline of the key events from your case files, with every event cited to its source."
        ctaLabel="Add Files"
        onCtaClick={() => {
          if (needsStartEditing) {
            setOpeningFilesForMatter(matterId)
            lease.startEditing()
          } else navigate(matterPaths.timeline.addFiles(matterId))
        }}
        ctaDisabled={isReadOnly || lease.pending || (!lease.canWrite && !needsStartEditing)}
        ctaLoading={needsStartEditing && lease.pending}
        ctaTooltip={isReadOnly ? buildMessage : undefined}
        notice={
          stagingTotal > 0
            ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
            : undefined
        }
        error={
          typeof initializeError === 'string'
            ? {
                headline: 'Timeline could not be generated.',
                detail: 'Please try again.',
                dismissLabel: 'Dismiss',
                onDismiss: () => dispatch(TimelineAssetActions.setInitializeError({ matterId, error: null })),
              }
            : undefined
        }
      />
    </div>
  )
}
